import { useState } from 'react';
import { AlertTriangle, Link2, Power, RefreshCw, RotateCcw, Sun } from 'lucide-react';
import api from '@/shared/api/client';
import { Button } from '@/shared/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { toast } from 'sonner';
import CourbeProductionPv from '@/features/assets/components/CourbeProductionPv';
import {
  dernierPointPvTexte, etatOnduleurTexte, mesureTexte, motifPvTexte,
  productionCouranteTexte,
} from '@/shared/utils/liveness';

/**
 * Fiche d'une centrale photovoltaïque : son relevé, son appariement, ses onduleurs.
 *
 * POURQUOI CE PANNEAU EXISTE
 *
 * Un actif PV était muet par construction jusqu'au 07/09 : aucun canal ne pouvait parler
 * pour lui. L'API S-Miles lui en donne un, mais seulement s'il est APPARIÉ à une centrale
 * du compte Hoymiles — et cet appariement ne se devine pas. Le numéro de série saisi sur
 * la centrale posée le 07/09 était faux, et apparier par le nom ou la capacité se
 * tromperait un jour dans le mauvais sens : en affichant la production d'une centrale sous
 * le nom d'une autre. C'est donc un geste d'exploitant, et voici l'écran qui le porte.
 *
 * ⚠️ DEUX BOUTONS QUI COÛTENT UN APPEL CHEZ HOYMILES, ET UN AFFICHAGE QUI N'EN COÛTE AUCUN
 *
 *   le RELEVÉ affiché        vient de l'actif, déposé par la veille. Gratuit.
 *   « Choisir une centrale » appelle le catalogue : un appel par page.
 *   « Voir les onduleurs »   appelle l'inventaire : un appel.
 *
 * Les deux derniers partagent le budget d'appels de la veille qui porte la PREUVE. D'où
 * des boutons, et non un chargement automatique à l'ouverture de la fiche : un panneau qui
 * se rafraîchirait tout seul viderait le budget et la production cesserait d'être
 * enregistrée — sans qu'un écran ne le dise.
 */
/** Le détail d'UN onduleur : ce que la route rend, sans rien sommer.

    ⚠️ LES PORTS CONTINUS NE SONT PAS ADDITIONNÉS, et c'est tout l'intérêt de cet écran :
    un total existe déjà à l'échelle de la centrale. Ce qu'on vient chercher ici, c'est le
    panneau qui produit moins que ses voisins — et une somme l'effacerait.

    Le dernier relevé de la journée est affiché en tête : au pas de quinze minutes, une
    journée en compte près de cent, et personne ne lit cent lignes. */
function DetailOnduleur({ releves }) {
  if (releves === null) {
    return <p className="text-[10px] text-red-600 mt-1">Détail indisponible.</p>;
  }
  if (!releves.length) {
    // Distingué d'un échec : l'appel a réussi, l'onduleur n'a simplement rien produit.
    return <p className="text-[10px] text-neutral-400 mt-1">Aucun relevé pour aujourd'hui.</p>;
  }
  const dernier = releves[releves.length - 1];
  const ac = dernier.ac || {};
  const dc = Array.isArray(dernier.dc) ? dernier.dc : [];
  return (
    <div className="mt-1 text-[10px] text-neutral-500 space-y-0.5" data-testid="pv-detail-onduleur">
      <p className="text-neutral-400">
        {releves.length} relevé(s) aujourd'hui · dernier à {dernier.time}
      </p>
      <p>
        Puissance {ac.p ?? '—'} W · Tension {ac.ua ?? '—'} V · Fréquence {ac.freq ?? '—'} Hz
        {' '}· Température {ac.temp ?? '—'} °C
      </p>
      {dc.map(port => (
        <p key={port.port} className="font-mono">
          panneau {port.port} : {port.p ?? '—'} W · {port.u ?? '—'} V · {port.i ?? '—'} A
        </p>
      ))}
    </div>
  );
}

export default function CentralePvPanel({ asset, peutApparier, onApparie }) {
  const [catalogue, setCatalogue] = useState(null);
  const [onduleurs, setOnduleurs] = useState(null);
  const [occupe, setOccupe] = useState(false);
  // Le detail d UN onduleur, charge au clic et garde par numero de serie. Jamais
  // precharge : l appel est PAYANT chez Hoymiles, un par onduleur.
  const [details, setDetails] = useState({});
  const [enCours, setEnCours] = useState(null);
  const apparie = Boolean(asset.centrale_smiles_id);

  const echec = (e, defaut) => {
    // Le motif du serveur est recopié tel quel : « clé absente », « clé refusée » et
    // « plafond atteint » appellent trois gestes différents, et un message générique n'en
    // ferait faire aucun.
    const detail = e?.response?.data?.detail;
    toast.error(typeof detail === 'object' ? (detail.message || defaut) : (detail || defaut));
  };

  const chargerCatalogue = async () => {
    setOccupe(true);
    try {
      const r = await api.get('/centrales-pv/catalogue');
      setCatalogue(r.data);
      if (!r.data.nombre) toast.info('Aucune centrale sur ce compte Hoymiles.');
    } catch (e) {
      echec(e, 'Catalogue des centrales indisponible');
    } finally { setOccupe(false); }
  };

  const chargerOnduleurs = async () => {
    setOccupe(true);
    try {
      const r = await api.get(`/centrales-pv/actifs/${asset.id}/micro-onduleurs`);
      setOnduleurs(r.data);
    } catch (e) {
      echec(e, 'Inventaire des onduleurs indisponible');
    } finally { setOccupe(false); }
  };

  // ⚠️ AU CLIC, JAMAIS AU CHARGEMENT. Un appel par onduleur, facture : une centrale de
  // quatorze unites en couterait quatorze a chaque ouverture de la fiche.
  const chargerDetail = async (serial) => {
    setEnCours(serial);
    try {
      const r = await api.get(
        `/centrales-pv/actifs/${asset.id}/micro-onduleurs/${serial}`);
      // ⚠️ Un tableau vide est une INFORMATION, pas un echec : l onduleur n a rien
      // produit ce jour-la. On le distingue d un appel qui a echoue, sans quoi les deux
      // s afficheraient pareil et « rien a signaler » couvrirait une panne.
      setDetails((d) => ({ ...d, [serial]: r.data?.releves || [] }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Le détail de cet onduleur n'a pas pu être lu");
      setDetails((d) => ({ ...d, [serial]: null }));
    } finally {
      setEnCours(null);
    }
  };

  // ⚠️ CE GESTE AGIT SUR LE MATERIEL D UN CLIENT, et Hoymiles route sur le SEUL numero de
  // serie : rien dans la requete ne nomme la centrale. Le serveur verifie le format avant
  // d appeler, parce qu un numero faux arrete l onduleur de quelqu un d autre.
  const commander = async (serial, commande, libelle) => {
    if (!window.confirm(`${libelle} ${serial} ?\n\nL'ordre part vers l'installation du client.`)) return;
    setEnCours(serial);
    try {
      const r = await api.post(
        `/centrales-pv/actifs/${asset.id}/micro-onduleurs/${serial}/${commande}`);
      // ⚠️ « Envoye », jamais « fait » : Hoymiles accuse reception, l execution reelle se
      // relit au tour de veille suivant. Le meme refus de peindre en vert que le bridage.
      toast.success(r.data?.confirme === false
        ? `${libelle} envoyé — exécution pas encore confirmée`
        : `${libelle} exécuté`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "L'ordre n'a pas été accepté");
    } finally {
      setEnCours(null);
    }
  };

  const poser = async (valeur) => {
    setOccupe(true);
    try {
      await api.put(`/centrales-pv/actifs/${asset.id}`, {
        centrale_smiles_id: valeur === '__aucune__' ? null : Number(valeur),
      });
      toast.success(valeur === '__aucune__'
        ? "Appariement retiré : cet actif redevient « inconnu »."
        : 'Centrale appariée. La première mesure arrive au prochain tour de veille.');
      onApparie?.();
    } catch (e) {
      echec(e, "Appariement refusé");
    } finally { setOccupe(false); }
  };

  return (
    <div className="p-4 rounded-lg border border-amber-200/80 bg-amber-50/20" data-testid="centrale-pv-panel">
      <h3 className="text-[13px] font-medium text-neutral-900 mb-1 flex items-center gap-1.5">
        <Sun className="w-3.5 h-3.5 text-amber-500" /> Centrale photovoltaïque
      </h3>

      {!apparie ? (
        <>
          {/* « Inconnu » et non « déconnecté » : un actif sans canal n'est pas une machine
              en panne, c'est une machine que personne n'interroge. La nuance est celle de
              `core/verite_materiel`, et elle change ce que l'exploitant doit faire. */}
          <p className="text-[11px] text-neutral-500 mb-3">
            Cet actif n'est apparié à aucune centrale S-Miles. Personne ne peut donc parler
            pour lui : il reste « inconnu », et c'est la vérité. Choisir sa centrale pour le
            faire parler.
          </p>
          {peutApparier && (catalogue ? (
            <Select onValueChange={poser} disabled={occupe}>
              <SelectTrigger className="h-8 text-[11px]" data-testid="pv-choix-centrale">
                <SelectValue placeholder="Choisir la centrale…" />
              </SelectTrigger>
              <SelectContent>
                {catalogue.centrales.map(c => (
                  <SelectItem key={c.centrale_smiles_id} value={String(c.centrale_smiles_id)}>
                    {c.nom || `Centrale ${c.centrale_smiles_id}`}
                    {c.capacite_kwc ? ` · ${c.capacite_kwc} kWc` : ''}
                    {/* Une centrale non achevée existe mais ne produit pas encore. Le
                        taire ferait conclure à une panne au premier relevé vide. */}
                    {c.achevee === false ? ' · non achevée' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Button size="sm" variant="outline" className="w-full h-8 text-[11px]"
                    onClick={chargerCatalogue} disabled={occupe}
                    data-testid="pv-charger-catalogue">
              <Link2 className="w-3 h-3 mr-1" /> Choisir une centrale
            </Button>
          ))}
          {catalogue && catalogue.complet === false && (
            <p className="text-[10px] text-amber-600 mt-2">
              Liste incomplète : toutes les pages du compte n'ont pas pu être lues. Si la
              centrale cherchée n'y est pas, réessayer.
            </p>
          )}
        </>
      ) : (
        <>
          <div className="space-y-1.5 text-[12px] mb-3">
            <p>
              <span className="text-neutral-400">Production :</span>{' '}
              <span className="font-medium text-amber-700">{productionCouranteTexte(asset)}</span>
            </p>
            <p><span className="text-neutral-400">Aujourd'hui :</span> {mesureTexte(asset.production_jour_kwh, 'kWh')}</p>
            <p><span className="text-neutral-400">Onduleur :</span> {etatOnduleurTexte(asset)}</p>
            {/* LA PUISSANCE INSTALLEE, telle que HOYMILES la declare — jamais celle saisie
                dans Flexcore. L'ecart mesure sur cette centrale meme, le 10/09 : 1 kW cote
                Flexcore contre 0,5 kWc cote Hoymiles. Afficher la premiere donnerait un
                taux d'utilisation faux de moitie, et rien ne le dirait. */}
            <p><span className="text-neutral-400">Puissance installée :</span> {mesureTexte(asset.capacite_kwc, 'kWc')}</p>
            {/* Ce que S-Miles nomme « rapport de puissance ». Il vaut « inconnu » quand la
                production courante n'est pas prouvee — un pourcentage calcule sur le
                dernier point du crepuscule s'afficherait toute la nuit, et un petit
                pourcentage nocturne ne choque personne : le mensonge passerait. */}
            <p><span className="text-neutral-400">Taux d'utilisation :</span> {mesureTexte(asset.taux_utilisation_pct, '%')}</p>
            {/* Le dernier point connu est montré SÉPARÉMENT de la production courante, et
                toujours avec son heure. L'API S-Miles ne rend aucune puissance
                instantanée : à 23 h son dernier point date du crépuscule, et le présenter
                comme « Production » annoncerait 4 kW en pleine nuit. */}
            {!asset.puissance_courante_prouvee && dernierPointPvTexte(asset) && (
              <p className="text-[11px] text-neutral-400">
                Dernier point connu : {dernierPointPvTexte(asset)}
              </p>
            )}
            <p className="text-[10px] text-neutral-400 font-mono">
              Centrale S-Miles n° {asset.centrale_smiles_id}
            </p>
          </div>

          <CourbeProductionPv points={asset.courbe_du_jour} />

          {motifPvTexte(asset) && (
            <p className="text-[11px] text-amber-700 mb-2 flex items-start gap-1">
              <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
              Pas de mesure : {motifPvTexte(asset)}
            </p>
          )}
          {(asset.alarmes || []).map(alarme => (
            <p key={alarme} className="text-[11px] text-red-600 mb-1 flex items-start gap-1">
              <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" /> {alarme}
            </p>
          ))}

          {onduleurs ? (
            <div className="pt-2 mt-2 border-t border-amber-100 space-y-1">
              <p className="text-[11px] text-neutral-500">
                {onduleurs.nombre_micro_onduleurs} micro-onduleur(s),{' '}
                {onduleurs.passerelles.length} passerelle(s)
              </p>
              {onduleurs.micro_onduleurs.map(mi => (
                <div key={mi.serial} className="border-t border-amber-50 pt-1">
                  <p className="text-[10px] font-mono text-neutral-400">{mi.serial}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    <Button size="sm" variant="outline" className="h-6 text-[10px] px-2"
                            onClick={() => chargerDetail(mi.serial)}
                            disabled={enCours === mi.serial}
                            data-testid={`pv-detail-${mi.serial}`}>
                      <RefreshCw className="w-3 h-3 mr-1" /> Détail
                    </Button>
                    <Button size="sm" variant="outline" className="h-6 text-[10px] px-2"
                            onClick={() => commander(mi.serial, 'micro_on', 'Démarrer')}
                            disabled={enCours === mi.serial}
                            data-testid={`pv-demarrer-${mi.serial}`}>
                      <Power className="w-3 h-3 mr-1" /> Démarrer
                    </Button>
                    <Button size="sm" variant="outline" className="h-6 text-[10px] px-2"
                            onClick={() => commander(mi.serial, 'micro_off', 'Arrêter')}
                            disabled={enCours === mi.serial}
                            data-testid={`pv-arreter-${mi.serial}`}>
                      <Power className="w-3 h-3 mr-1" /> Arrêter
                    </Button>
                    <Button size="sm" variant="outline" className="h-6 text-[10px] px-2"
                            onClick={() => commander(mi.serial, 'micro_restart', 'Redémarrer')}
                            disabled={enCours === mi.serial}
                            data-testid={`pv-redemarrer-${mi.serial}`}>
                      <RotateCcw className="w-3 h-3 mr-1" /> Redémarrer
                    </Button>
                  </div>
                  {details[mi.serial] !== undefined && (
                    <DetailOnduleur releves={details[mi.serial]} />
                  )}
                </div>
              ))}
            </div>
          ) : (
            <Button size="sm" variant="outline" className="w-full h-8 text-[11px]"
                    onClick={chargerOnduleurs} disabled={occupe}
                    data-testid="pv-charger-onduleurs">
              <RefreshCw className="w-3 h-3 mr-1" /> Voir les onduleurs
            </Button>
          )}

          {peutApparier && (
            <Button size="sm" variant="ghost"
                    className="w-full h-7 text-[10px] text-neutral-400 mt-1"
                    onClick={() => poser('__aucune__')} disabled={occupe}
                    data-testid="pv-retirer-appariement">
              Retirer l'appariement
            </Button>
          )}
        </>
      )}
    </div>
  );
}
